import { createContext, useState, useContext } from "react";
import { schedule } from "../api/TaskSchedule";
import { getGanttChartSubtasks } from "../components/gantt/GanttChart";
import { getUserTasks } from "../api/TaskCRUD.ts";

const TaskContext = createContext(null);

export const useTask = () => {
  return useContext(TaskContext);
};

export const TaskProvider = ({ children }) => {
  const [tasks, setTasks] = useState([]);
  const [ganttChartState, setGanttChartState] = useState({
    taskID: "",
    taskTitle: "",
    subtasks: [],
    totalDuration: null,
  });
  const [workerCount, setWorkerCount] = useState(1);
  const [selectedSubtask, setSelectedSubtask] = useState(null);

  const updateGanttChart = ganttInfo => {
    setGanttChartState(prevState => ({
      taskID: ganttInfo.taskID || prevState.taskID,
      taskTitle: ganttInfo.taskTitle || prevState.taskTitle,
      subtasks: getGanttChartSubtasks(ganttInfo.subtasks),
      totalDuration: ganttInfo.totalDuration,
    }));
  };

  async function ensureTasksLoaded() {
    if (tasks.length > 0) {
      return tasks; // Tasks are already loaded
    }

    let loadedTasks = loadLocalTasks();

    if (loadedTasks.length === 0) {
      loadedTasks = await loadRemoteTasks();
    }

    setTasks(loadedTasks);
    return loadedTasks; // Return the loaded tasks
  }

  const fetchAndScheduleTask = async (taskID, currentWorkerCount) => {
    const loadedTasks = await ensureTasksLoaded(); // Await and get the loaded tasks
    const task = loadedTasks.find(t => t.taskID === taskID);
    if (!task) {
      console.error("Task not found:", taskID);
      return;
    }

    try {
      const scheduledTaskData = await schedule(task, currentWorkerCount);
      const ganttChartSubtasks = getGanttChartSubtasks(
        scheduledTaskData.subtasks,
      );
      setGanttChartState({
        taskID: scheduledTaskData.taskID,
        taskTitle: scheduledTaskData.taskTitle,
        subtasks: ganttChartSubtasks,
        totalDuration: scheduledTaskData.totalDuration,
      });
    } catch (error) {
      console.error("Failed to fetch task data:", error);
    }
  };

  // Append to or update the task state given a particular task
  const updateTaskState = task => {
    setTasks(prevTasks => {
      const taskIndex = prevTasks.findIndex(t => t.taskID === task.taskID);
      if (taskIndex !== -1) {
        // Task exists, update it
        const newTasks = [...prevTasks];
        newTasks[taskIndex] = task;
        return newTasks;
      } else {
        // Task does not exist, add to the top of the list
        return [task, ...prevTasks];
      }
    });
  };

  const saveLocalTasks = () => {
    localStorage.setItem("tasks", JSON.stringify(tasks));
  };

  // Utility to load tasks from local storage
  const loadLocalTasks = () => {
    const tasks = localStorage.getItem("tasks");
    return tasks ? JSON.parse(tasks) : [];
  };

  const loadRemoteTasks = async () => {
    const userTasks = await getUserTasks();
    return userTasks;
  };

  return (
    <TaskContext.Provider
      value={{
        tasks,
        setTasks,
        updateTaskState,
        ensureTasksLoaded,
        saveLocalTasks,
        loadRemoteTasks,
        ganttChartState,
        setGanttChartState,
        workerCount,
        setWorkerCount,
        selectedSubtask,
        setSelectedSubtask,
        updateGanttChart,
        fetchAndScheduleTask,
      }}
    >
      {children}
    </TaskContext.Provider>
  );
};
